<template>
  <div ref="pageRef" class="index-page">
    <div class="mobile-shell">
      <div class="mobile-bg-glow mobile-bg-glow-1"></div>
      <div class="mobile-bg-glow mobile-bg-glow-2"></div>

      <div class="mobile-screen">
        <template v-if="activeMobileTab === 'discover'">
          <header class="mobile-topbar">
            <div class="mobile-search">
              <font-awesome-icon icon="magnifying-glass" class="mobile-search-icon" />
              <input type="text" placeholder="搜索视频..." class="mobile-search-input" />
            </div>
            <button type="button" class="mobile-avatar-btn">
              <div class="mobile-avatar-thumb">
                <font-awesome-icon icon="seedling" />
              </div>
            </button>
          </header>

          <section class="mobile-hero">
            <h2 class="mobile-hero-title">欢迎回来，小树苗</h2>
            <p class="mobile-hero-subtitle">今天是 {{ currentDate }}，今天也要快乐学习哦！</p>
          </section>

          <section class="mobile-stage-list">
            <article
              v-for="stage in learningStages"
              :key="`mobile-${stage.id}`"
              class="mobile-stage-card"
              :class="{ 'has-stage-image': stage.image }"
              :style="{
                '--mobile-stage-color': stage.color,
                '--mobile-stage-bg': stage.bgColor,
                '--mobile-stage-image': stage.image ? `url(${stage.image})` : 'none',
                '--stage-image-edge-color': stage.imageEdgeColor,
                '--stage-image-bg-start': stage.imageBgStart,
                '--stage-image-bg-end': stage.imageBgEnd
              }"
              @click="handleStageSelect(stage)"
            >
              <div class="mobile-stage-visual">
                <div class="mobile-stage-icon" :style="{ background: stage.bgColor }">
                  <img v-if="stage.image" :src="stage.image" alt="" class="mobile-stage-image" />
                  <font-awesome-icon v-else :icon="stage.icon" />
                </div>
              </div>
              <div class="mobile-stage-copy">
                <span class="mobile-stage-age">{{ stage.ageRange }}</span>
                <h3 class="mobile-stage-title">【{{ stage.title }}】</h3>
                <p class="mobile-stage-desc">{{ stage.subtitle }}</p>
              </div>
              <button type="button" class="mobile-stage-play" aria-label="进入阶段">
                <font-awesome-icon icon="play" />
              </button>
            </article>
          </section>
        </template>

        <template v-else-if="activeMobileTab === 'explore'">
          <header class="mobile-topbar">
            <div class="mobile-search">
              <font-awesome-icon icon="magnifying-glass" class="mobile-search-icon" />
              <input type="text" placeholder="搜索视频..." class="mobile-search-input" />
            </div>
            <button type="button" class="mobile-avatar-btn">
              <div class="mobile-avatar-thumb">
                <font-awesome-icon icon="seedling" />
              </div>
            </button>
          </header>

          <section class="mobile-discovery-list">
            <button type="button" class="mobile-discovery-card mobile-discovery-card-video" @click="handleMobileDiscoveryNavigate('/videos')">
              <span class="mobile-discovery-visual mobile-discovery-tv">
                <span class="mobile-tv-body">
                  <span class="mobile-tv-play">
                    <font-awesome-icon icon="play" />
                  </span>
                </span>
              </span>
              <span class="mobile-discovery-copy">
                <span class="mobile-discovery-title">全部视频</span>
                <span class="mobile-discovery-desc">海量视频资源，随时随地观看</span>
                <span class="mobile-discovery-action">去看看 <font-awesome-icon icon="arrow-right" /></span>
              </span>
              <span class="mobile-discovery-arrow">
                <font-awesome-icon icon="chevron-right" />
              </span>
            </button>

            <button type="button" class="mobile-discovery-card mobile-discovery-card-flash" @click="handleMobileDiscoveryNavigate('/flash')">
              <span class="mobile-discovery-visual mobile-discovery-flashcards">
                <span class="mobile-flashcard mobile-flashcard-back"></span>
                <span class="mobile-flashcard mobile-flashcard-mid"></span>
                <span class="mobile-flashcard mobile-flashcard-front">Aa</span>
              </span>
              <span class="mobile-discovery-copy">
                <span class="mobile-discovery-title">单词闪卡</span>
                <span class="mobile-discovery-desc">趣味记忆单词，学习更高效</span>
                <span class="mobile-discovery-action">去学习 <font-awesome-icon icon="arrow-right" /></span>
              </span>
              <span class="mobile-discovery-arrow">
                <font-awesome-icon icon="chevron-right" />
              </span>
            </button>

            <button
              type="button"
              class="mobile-discovery-card mobile-discovery-card-song"
              @click="handleMobileDiscoveryNavigate({ path: '/audios', query: { title: '启蒙儿歌音频库', stage: '1,2' } })"
            >
              <span class="mobile-discovery-visual mobile-discovery-music">
                <span class="mobile-music-body">
                  <span class="mobile-music-note">
                    <font-awesome-icon icon="music" />
                  </span>
                </span>
              </span>
              <span class="mobile-discovery-copy">
                <span class="mobile-discovery-title">全部启蒙儿歌</span>
                <span class="mobile-discovery-desc">精选启蒙儿歌，快乐轻松学</span>
                <span class="mobile-discovery-action">去听听 <font-awesome-icon icon="arrow-right" /></span>
              </span>
              <span class="mobile-discovery-arrow">
                <font-awesome-icon icon="chevron-right" />
              </span>
            </button>

            <button
              type="button"
              class="mobile-discovery-card mobile-discovery-card-book"
              @click="handleMobileDiscoveryNavigate({ path: '/audios', query: { title: '有声书音频库', stage: '3,4' } })"
            >
              <span class="mobile-discovery-visual mobile-discovery-audiobook">
                <span class="mobile-book-body">
                  <span class="mobile-book-headphones">
                    <font-awesome-icon icon="headphones" />
                  </span>
                  <span class="mobile-book-page">
                    <font-awesome-icon icon="book-open" />
                  </span>
                </span>
              </span>
              <span class="mobile-discovery-copy">
                <span class="mobile-discovery-title">全部有声书</span>
                <span class="mobile-discovery-desc">优质有声读物，畅听精彩故事</span>
                <span class="mobile-discovery-action">去收听 <font-awesome-icon icon="arrow-right" /></span>
              </span>
              <span class="mobile-discovery-arrow">
                <font-awesome-icon icon="chevron-right" />
              </span>
            </button>
          </section>
        </template>

        <template v-else-if="activeMobileTab === 'progress'">
          <section class="mobile-progress-head">
            <h2 class="mobile-section-heading">学习进度</h2>
          </section>

          <template v-for="item in progressCards" :key="`mobile-progress-${item.id}`">
            <section v-if="item.id === 'calendar'" class="mobile-panel-card">
              <div class="mobile-panel-head">
                <h3 class="mobile-panel-title">宝贝每日打卡历</h3>
                <span class="mobile-panel-badge">连续打卡 {{ consecutiveCheckInDays }} 天</span>
              </div>

              <div class="mobile-calendar-grid">
                <div v-for="day in weekDays" :key="`mobile-week-${day}`" class="mobile-calendar-weekday">
                  {{ day }}
                </div>
                <div
                  v-for="(date, index) in calendarDays"
                  :key="`mobile-date-${index}`"
                  :class="[
                    'mobile-calendar-day',
                    {
                      'is-today': date === today,
                      'is-checked': checkInDays.includes(date),
                      'is-empty': date === 0
                    }
                  ]"
                >
                  <template v-if="date">
                    <span class="mobile-calendar-number">{{ date }}</span>
                    <font-awesome-icon
                      v-if="checkInDays.includes(date)"
                      icon="star"
                      class="mobile-calendar-star"
                    />
                  </template>
                </div>
              </div>

              <button
                type="button"
                class="mobile-check-in-btn"
                :class="{ checked: hasCheckedInToday }"
                :disabled="hasCheckedInToday || isCheckInLoading"
                @click="handleCheckIn"
              >
                {{ isCheckInLoading ? '签到中...' : hasCheckedInToday ? '今日已签到' : '签到' }}
              </button>
            </section>

            <section v-else-if="item.id === 'weekly-time'" class="mobile-panel-card">
              <div class="mobile-panel-head mobile-panel-head-chart">
                <h3 class="mobile-panel-title">本周学习时长</h3>
                <span class="mobile-panel-meta">单位: 分钟</span>
              </div>
              <div ref="mobileChartRef" class="mobile-chart-container"></div>
            </section>

            <section v-else-if="item.id === 'vocab'" class="mobile-panel-card mobile-vocab-card">
              <div class="mobile-vocab-deco mobile-vocab-deco-top">
                <font-awesome-icon icon="book-open" />
              </div>
              <div class="mobile-vocab-metric">
                <h3 class="mobile-panel-title">累计已学习词汇量</h3>
                <p class="mobile-vocab-number">0</p>
              </div>
              <div class="mobile-vocab-metric">
                <h3 class="mobile-panel-title">累积学习时长（分钟）</h3>
                <p class="mobile-vocab-number">0</p>
              </div>
            </section>
          </template>
        </template>

        <template v-else>
          <section class="mobile-profile-top">
            <div class="mobile-profile-avatar">
              <div class="mobile-profile-avatar-inner">
                <font-awesome-icon icon="seedling" />
              </div>
            </div>
            <h2 class="mobile-profile-name">小树苗用户</h2>
            <div class="mobile-membership-card">
              <div class="mobile-membership-copy">
                <div class="mobile-membership-title">
                  <font-awesome-icon :icon="membershipIconName" class="mobile-membership-crown" />
                  <span>{{ membershipTitleText }}</span>
                </div>
                <p
                  v-if="!isGuestUser && hasVipExpireTime"
                  class="mobile-membership-expiry"
                  :class="{ expired: !userInfo.vipTag }"
                >
                  {{ mobileMembershipExpiryText }}
                </p>
              </div>
              <button v-if="!isGuestUser" type="button" class="mobile-membership-link" @click="router.push('/vip')">
                {{ mobileMembershipButtonText }}
                <font-awesome-icon icon="chevron-right" />
              </button>
              <button v-if="isGuestUser" type="button" class="mobile-membership-link" @click="router.push('/login')">
                立即登录
                <font-awesome-icon icon="chevron-right" />
              </button>
            </div>
          </section>

          <section class="mobile-profile-menu">
            <button
              v-for="item in mobileProfileItems"
              :key="item.id"
              type="button"
              class="mobile-profile-item"
              :class="{ 'is-danger': item.danger }"
              @click="handleProfileItemClick(item)"
            >
              <span class="mobile-profile-item-icon" :style="{ '--profile-accent': item.color }">
                <font-awesome-icon :icon="item.icon" />
              </span>
              <span class="mobile-profile-item-label">{{ item.label }}</span>
              <font-awesome-icon icon="chevron-right" class="mobile-profile-item-arrow" />
            </button>
          </section>
        </template>

        <nav class="mobile-tabbar">
          <button
            v-for="tab in mobileTabs"
            :key="tab.id"
            type="button"
            class="mobile-tabbar-item"
            :class="{ active: activeMobileTab === tab.id }"
            @click="handleMobileTabClick(tab.id)"
          >
            <font-awesome-icon :icon="tab.icon" class="mobile-tabbar-icon" />
            <span>{{ tab.label }}</span>
          </button>
        </nav>
      </div>
    </div>

    <!-- 移动端菜单按钮 -->
    <button class="mobile-menu-btn" @click="showMobileMenu = !showMobileMenu">
      <font-awesome-icon :icon="showMobileMenu ? 'times' : 'bars'" />
    </button>

    <!-- 背景装饰 -->
    <div class="bg-decoration">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
    </div>

    <!-- 主要内容区域 -->
    <div class="content-wrapper">
      <!-- 左侧导航 -->
      <aside class="sidebar" :class="{ 'mobile-open': showMobileMenu }">
        <nav class="nav-card">
          <!-- Logo区域 -->
          <div class="nav-logo">
            <div class="logo-icon">
              <img :src="logo2Image" alt="" class="logo2-icon" />
            </div>
            <div class="brand-copy">
              <h1 class="brand-name">树苗英语</h1>
              <span class="brand-subtitle">TREE SAPLING</span>
            </div>
          </div>
          
          <ul class="nav-list">
            <li v-for="item in navItems" :key="item.id">
              <a
                href="#"
                @click.prevent="handleNavItemClick(item)"
                :class="['nav-item', { active: activeNav === item.id }]"
              >
                <font-awesome-icon :icon="item.icon" class="nav-icon" />
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>

          <div class="nav-menu-visual" aria-hidden="true">
            <img :src="menuBgImage" alt="" class="nav-menu-image" />
          </div>

        </nav>
      </aside>

      <!-- 移动端遮罩 -->
      <div v-if="showMobileMenu" class="mobile-overlay" @click="showMobileMenu = false"></div>

        <!-- 右侧主内容 -->
        <div class="right-wrapper">
          <!-- 顶部欢迎栏 -->
          <div class="glass-card welcome-bar">
            <div class="welcome-left">
              <h2 class="welcome-text">
                欢迎回来，小树苗 <span class="wave">👋</span>
              </h2>
              <p class="date-text">今天是 {{ currentDate }}，今天也要快乐学习哦！</p>
            </div>
            <div class="welcome-actions">
              <button
                v-if="!isGuestUser"
                type="button"
                class="top-account-badge"
                aria-label="我的账号"
                @click="router.push('/myprofile')"
              >
                <span class="top-account-avatar">
                  <font-awesome-icon icon="seedling" />
                </span>
                <span class="top-account-label">我的账号</span>
              </button>
              <div class="top-membership-card">
                <div class="top-membership-copy">
                  <div class="top-membership-title">
                    <font-awesome-icon :icon="membershipIconName" class="top-membership-crown" />
                    <span>{{ membershipTitleText }}</span>
                  </div>
                  <p
                    v-if="!isGuestUser && hasVipExpireTime"
                    class="top-membership-expiry"
                    :class="{ expired: !userInfo.vipTag }"
                  >
                    {{ topMembershipExpiryText }}
                  </p>
                </div>
                <button v-if="!isGuestUser" type="button" class="top-membership-btn" @click="router.push('/vip')">
                  {{ topMembershipButtonText }}
                </button>
                <button v-if="isGuestUser" type="button" class="top-membership-btn" @click="router.push('/login')">
                  {{ topMembershipButtonText }}
                </button>
              </div>
            </div>
          </div>

          <!-- 内容区域 -->
          <div class="content-area">
            <!-- 左侧内容 -->
            <div class="left-content">
              <!-- 学习阶段卡片 -->
              <section class="section-card">
                <h3 class="section-title">选择学习阶段</h3>
                <div class="stage-grid">
                  <div
                    v-for="stage in learningStages"
                    :key="stage.id"
                    class="stage-card"
                    :class="{ 'has-stage-image': stage.image }"
                    :style="{
                      '--stage-color': stage.color,
                      '--stage-image-edge-color': stage.imageEdgeColor,
                      '--stage-card-image': stage.image ? `url(${stage.image})` : 'none',
                      '--stage-image-bg-start': stage.imageBgStart,
                      '--stage-image-bg-end': stage.imageBgEnd
                    }"
                    @click="handleStageSelect(stage)"
                  >
                    <div class="stage-icon" :style="{ background: stage.bgColor }">
                      <img v-if="stage.image" :src="stage.image" alt="" class="stage-image" />
                      <font-awesome-icon v-else :icon="stage.icon" />
                    </div>
                    <div class="stage-info">
                      <h4 class="stage-title">{{ stage.title }}</h4>
                      <p class="stage-desc">{{ stage.subtitle }}</p>
                      <span class="stage-tag" :style="{ background: stage.bgColor, color: stage.color }">
                        {{ stage.ageRange }}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              <!-- 数据卡片区域 -->
              <div class="data-grid">
                <div class="glass-card data-card">
                  <div class="data-content">
                    <div class="data-metrics">
                      <div class="data-metric">
                        <h4 class="data-title">累计已学习词汇量</h4>
                        <span class="number">0</span>
                      </div>
                      <div class="data-metric">
                        <h4 class="data-title">累积学习时长（分钟）</h4>
                        <span class="number">0</span>
                      </div>
                    </div>
                    <div class="data-deco">
                      <font-awesome-icon icon="book-open" />
                    </div>
                  </div>
                </div>

                <div class="glass-card chart-card">
                  <h4 class="data-title">本周学习数据（分钟）</h4>
                  <div ref="chartRef" class="chart-container"></div>
                </div>
              </div>

            </div>

            <!-- 右侧边栏 -->
            <div class="right-panel">
              <div class="quick-entry-list" aria-label="快捷入口">
                <button type="button" class="glass-card quick-entry-card" @click="router.push('/videos')">
                  <span class="quick-entry-icon quick-entry-icon-video">
                    <font-awesome-icon icon="play" />
                  </span>
                  <span class="quick-entry-copy">
                    <span class="quick-entry-title">全部视频</span>
                    <span class="quick-entry-desc">精选英语启蒙视频</span>
                  </span>
                </button>

                <button type="button" class="glass-card quick-entry-card" @click="router.push('/flash')">
                  <span class="quick-entry-icon quick-entry-icon-flash">
                    <font-awesome-icon icon="bolt" />
                  </span>
                  <span class="quick-entry-copy">
                    <span class="quick-entry-title">单词闪卡</span>
                    <span class="quick-entry-desc">趣味记忆单词</span>
                  </span>
                </button>
              </div>

              <button
                type="button"
                class="glass-card quick-entry-card"
                @click="router.push({ path: '/audios', query: { title: '启蒙儿歌音频库', stage: '1,2' } })"
              >
                <span class="quick-entry-icon quick-entry-icon-song">
                  <font-awesome-icon icon="music" />
                </span>
                <span class="quick-entry-copy">
                  <span class="quick-entry-title">全部启蒙儿歌</span>
                  <span class="quick-entry-desc">精选启蒙儿歌</span>
                </span>
              </button>

              <button
                type="button"
                class="glass-card quick-entry-card"
                @click="router.push({ path: '/audios', query: { title: '有声书音频库', stage: '3,4' } })"
              >
                <span class="quick-entry-icon quick-entry-icon-book">
                  <font-awesome-icon icon="headphones" />
                </span>
                <span class="quick-entry-copy">
                  <span class="quick-entry-title">全部有声书</span>
                  <span class="quick-entry-desc">优质有声读物</span>
                </span>
              </button>

              <!-- 打卡日历 -->
              <div class="glass-card calendar-card">
                <div class="calendar-header">
                  <h4 class="calendar-title">打卡日历</h4>
                  <span class="calendar-month">{{ currentMonth }}</span>
                </div>
                <div class="calendar-grid">
                  <div v-for="day in weekDays" :key="day" class="calendar-weekday">{{ day }}</div>
                  <div
                    v-for="(date, index) in calendarDays"
                    :key="index"
                    :class="['calendar-day', {
                      'is-today': date === today,
                      'is-checked': checkInDays.includes(date),
                      'is-empty': date === 0
                    }]"
                  >
                    {{ date || '' }}
                  </div>
                </div>
                <div class="streak-tip">
                  <div class="streak-main">
                    <font-awesome-icon icon="fire" class="streak-icon" />
                    <div class="streak-info">
                      <span class="streak-count">已连续{{ consecutiveCheckInDays }}天</span>
                      <span class="streak-hint">{{ hasCheckedInToday ? '' : '继续保持哦！' }}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="check-in-btn"
                    :class="{ checked: hasCheckedInToday }"
                    :disabled="hasCheckedInToday || isCheckInLoading"
                    @click="handleCheckIn"
                  >
                    {{ isCheckInLoading ? '签到中...' : hasCheckedInToday ? '今日已签到' : '签到' }}
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

    <ParentPasswordModal ref="parentPasswordModalRef" @verified="activeNav = 'parent'" />

    <LoginPromptModal v-model="isLoginPromptOpen" @confirm="goLogin" />

    <Transition name="checkin-toast">
      <div v-if="checkInToastMessage" class="checkin-toast">
        {{ checkInToastMessage }}
      </div>
    </Transition>

    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import * as echarts from 'echarts'
import ParentPasswordModal from '../components/ParentPasswordModal.vue'
import LoginPromptModal from '../components/LoginPromptModal.vue'
import logo2Image from '../assets/logo2.png'
import menuBgImage from '../assets/menu_bg.jpg'
import stage1Image from '../assets/stage_1.jpg'
import stage2Image from '../assets/stage_2.jpg'
import stage3Image from '../assets/stage_3.jpg'
import stage4Image from '../assets/stage_4.jpg'
import * as UserApi from '@/api/user'
import { getAccessToken } from '@/config/axios/auth'

const STAGE_ORDER_STORAGE_KEY = 'tree-sprout-stage-order'
const PROGRESS_ORDER_STORAGE_KEY = 'tree-sprout-progress-card-order'

const router = useRouter()
const route = useRoute()

const today = new Date().getDate()
const currentDate = computed(() => {
  const now = new Date()
  return `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日`
})
const currentMonth = computed(() => {
  const now = new Date()
  return `${now.getMonth() + 1}月`
})
const weekDays = ['日', '一', '二', '三', '四', '五', '六']

const activeNav = ref('home')
const activeMobileTab = ref('discover')
const showMobileMenu = ref(false)
const pageRef = ref(null)
const checkInToastMessage = ref('')
const parentPasswordModalRef = ref(null)
const isCheckInLoading = ref(false)
const isLoginPromptOpen = ref(false)
let checkInToastTimer = null
const guestProtectedMenuIds = new Set(['history', 'favorites', 'settings', 'parent', 'account'])

const navItems = [
  { id: 'home', label: '首页', icon: 'home' },
  { id: 'history', label: '观看记录', icon: 'play-circle' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart' },
  { id: 'settings', label: '偏好设置', icon: 'cog' },
  { id: 'parent', label: '家长模式', icon: 'user-shield' },
]


const mobileTabs = [
  { id: 'discover', label: '首页', icon: 'home' },
  { id: 'explore', label: '发现', icon: 'compass' },
  { id: 'progress', label: '打卡', icon: 'calendar-days' },
  { id: 'profile', label: '我的', icon: 'user' },
]

const syncMobileTabFromRoute = () => {
  const tab = typeof route.query.tab === 'string' ? route.query.tab : ''

  if (mobileTabs.some((item) => item.id === tab)) {
    activeMobileTab.value = tab
  }
}

const replaceMobileTabQuery = (tab) => {
  return router.replace({
    path: route.path,
    query: {
      ...route.query,
      tab
    }
  })
}

const handleMobileTabClick = (tab) => {
  activeMobileTab.value = tab
  replaceMobileTabQuery(tab)
}

const handleMobileDiscoveryNavigate = async (to) => {
  activeMobileTab.value = 'explore'
  if (route.query.tab !== 'explore') {
    await replaceMobileTabQuery('explore')
  }
  router.push(to)
}

const mobileProfileItems = [
  { id: 'history', label: '观看记录', icon: 'clock-rotate-left', color: '#8db8de' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart', color: '#df89b8' },
  { id: 'settings', label: '偏好设置', icon: 'gear', color: '#9b91d8' },
  { id: 'parent', label: '家长模式', icon: 'lock', color: '#d7a15f' },
  { id: 'account', label: '我的账号', icon: 'user', color: '#79b88d' },
]

const userInfo = ref({
  userId: null,
  vipExpireTime: '',
  vipTag: false,
})

const isGuestUser = computed(() => getAccessToken() == null)

const hasVipExpireTime = computed(() => userInfo.value.vipExpireTime != null && userInfo.value.vipExpireTime !== '')

const membershipIconName = computed(() => hasVipExpireTime.value ? 'crown' : 'user')

const membershipTitleText = ref('游客')

const mobileMembershipButtonText = computed(() => {
  if (isGuestUser.value) {
    return '立即登录'
  }

  return hasVipExpireTime.value ? '会员中心' : '开通会员'
})

const topMembershipButtonText = ref('立即登录')

watch(
  () => [isGuestUser.value, hasVipExpireTime.value],
  ([isGuest, hasExpireTime]) => {
    if (isGuest) {
      membershipTitleText.value = '游客'
      topMembershipButtonText.value = '立即登录'
      return
    }

    membershipTitleText.value = hasExpireTime ? '树苗会员' : '普通用户'
    topMembershipButtonText.value = hasExpireTime ? '立即续费' : '开通会员'
  },
  { immediate: true }
)

const mobileMembershipExpiryText = computed(() => {
  const expireTime = userInfo.value.vipExpireTime

  if (!userInfo.value.vipTag) {
    return '会员已过期'
  }

  return expireTime ? `会员有效期至 ${expireTime}` : '会员有效期获取中'
})

const topMembershipExpiryText = computed(() => {
  const expireTime = userInfo.value.vipExpireTime

  if (!userInfo.value.vipTag && expireTime !==null) {
    return '会员已过期'
  }
  return expireTime ? `有效期至 ${expireTime}` : '有效期获取中'
})


const openParentPasswordModal = () => {
  parentPasswordModalRef.value?.open()
}

const openLoginPrompt = () => {
  isLoginPromptOpen.value = true
}

const closeLoginPrompt = () => {
  isLoginPromptOpen.value = false
}

const goLogin = () => {
  closeLoginPrompt()
  router.push('/login')
}

const shouldPromptLogin = (item) => {
  return isGuestUser.value && guestProtectedMenuIds.has(item.id)
}

const handleNavItemClick = (item) => {
  showMobileMenu.value = false

  if (shouldPromptLogin(item)) {
    openLoginPrompt()
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
    return
  }

  activeNav.value = item.id
}

const handleProfileItemClick = (item) => {
  if (shouldPromptLogin(item)) {
    openLoginPrompt()
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'progress') {
    router.replace({
      path: '/home',
      query: {
        ...route.query,
        tab: 'progress'
      }
    })
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'account') {
    router.push('/myprofile')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
    return
  }

  activeNav.value = item.id
}

const handleStageSelect = (stage) => {
  router.push({
    path: '/category',
    query: { stage: stage.id }
  })
}

const defaultLearningStages = [
  { id: '1', title: '启蒙感知', subtitle: '通过趣味动画和儿歌培养英语兴趣', ageRange: '0-3岁', icon: 'palette', image: stage1Image, imageEdgeColor: '#f9f9fc', imageBgStart: '#fbf1f0', imageBgEnd: '#fffafa', color: '#5B8FF9', bgColor: 'rgba(91, 143, 249, 0.15)' },
  { id: '2', title: '语言萌芽', subtitle: '基础词汇和简单句型学习', ageRange: '3-6岁', icon: 'sprout', image: stage2Image, imageEdgeColor: '#faf8e5', imageBgStart: '#eef5e7', imageBgEnd: '#fbfdf7', color: '#4CAF50', bgColor: 'rgba(76, 175, 80, 0.15)' },
  { id: '3', title: '表达发展', subtitle: '口语表达和简单对话练习', ageRange: '6-9岁', icon: 'comments', image: stage3Image, imageEdgeColor: '#fcf3ec', imageBgStart: '#f7eee2', imageBgEnd: '#fffaf3', color: '#FFB020', bgColor: 'rgba(255, 176, 32, 0.15)' },
  { id: '4', title: '能力提升', subtitle: '阅读理解和简单书写', ageRange: '9-12岁', icon: 'book', image: stage4Image, imageEdgeColor: '#f4f0f0', imageBgStart: '#f0f0fb', imageBgEnd: '#fbfaff', color: '#9C6ADE', bgColor: 'rgba(156, 106, 222, 0.15)' },
]

const stagePreferenceIdMap = {
  sense: '1',
  sprout: '2',
  expression: '3',
  ability: '4',
}

const normalizeStageOrderId = (id) => stagePreferenceIdMap[id] || String(id)

const getSavedStageOrder = () => {
  const defaultIds = defaultLearningStages.map((stage) => stage.id)

  try {
    const savedOrder = JSON.parse(window.localStorage.getItem(STAGE_ORDER_STORAGE_KEY) || '[]')
    const validSavedIds = Array.isArray(savedOrder)
      ? savedOrder.map(normalizeStageOrderId).filter((id) => defaultIds.includes(id))
      : []

    return [...new Set([...validSavedIds, ...defaultIds])]
  } catch {
    return defaultIds
  }
}

const learningStages = computed(() => {
  const order = getSavedStageOrder()
  return order
    .map((id) => defaultLearningStages.find((stage) => stage.id === id))
    .filter(Boolean)
})

const signSummary = ref(null)
const checkInDays = computed(() => {
  const signedDays = signSummary.value?.signedDays
  return Array.isArray(signedDays) ? signedDays : []
})
const hasCheckedInToday = computed(() => Boolean(signSummary.value?.todaySigned))
const consecutiveCheckInDays = computed(() => signSummary.value?.continuousDays || 0)

const showCheckInToast = (message) => {
  checkInToastMessage.value = message
  window.clearTimeout(checkInToastTimer)
  checkInToastTimer = window.setTimeout(() => {
    checkInToastMessage.value = ''
  }, 2000)
}

const getCurrentSignParams = () => {
  const now = new Date()
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1
  }
}

const getSignSummary = async () => {
  const { year, month } = getCurrentSignParams()
  try {
    signSummary.value = await UserApi.getSignSummary(year, month)
    return true
  } catch (error) {
    return false
  }
}

const handleCheckIn = async () => {
  if (isGuestUser.value) {
    openLoginPrompt()
    return
  }

  if (isCheckInLoading.value) {
    return
  }

  if (hasCheckedInToday.value) {
    showCheckInToast(`已连续签到${consecutiveCheckInDays.value}天`)
    return
  }

  isCheckInLoading.value = true
  try {
    await UserApi.addSign()
    const isSummaryUpdated = await getSignSummary()
    showCheckInToast(
      isSummaryUpdated
        ? `签到成功，已连续签到${consecutiveCheckInDays.value}天`
        : '签到成功'
    )
  } catch (error) {
    if (!error?.notified) {
      showCheckInToast('签到失败，请稍后重试')
    }
  } finally {
    isCheckInLoading.value = false
  }
}

const calendarDays = computed(() => {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth()
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const days = new Array(firstDay).fill(0)
  for (let i = 1; i <= daysInMonth; i++) { days.push(i) }
  return days
})

const defaultProgressCards = [
  { id: 'calendar' },
  { id: 'weekly-time' },
  { id: 'vocab' },
]

const getSavedProgressCardOrder = () => {
  const defaultIds = defaultProgressCards.map((item) => item.id)

  try {
    const savedOrder = JSON.parse(window.localStorage.getItem(PROGRESS_ORDER_STORAGE_KEY) || '[]')
    const validSavedIds = Array.isArray(savedOrder)
      ? savedOrder.filter((id) => defaultIds.includes(id))
      : []

    return [...new Set([...validSavedIds, ...defaultIds])]
  } catch {
    return defaultIds
  }
}

const progressCards = computed(() => {
  const order = getSavedProgressCardOrder()
  return order
    .map((id) => defaultProgressCards.find((item) => item.id === id))
    .filter(Boolean)
})

const chartRef = ref(null)
const mobileChartRef = ref(null)
let desktopChart = null
let mobileChart = null

const chartOption = {
  grid: { left: '3%', right: '4%', bottom: '3%', top: '10%', containLabel: true },
  xAxis: {
    type: 'category',
    boundaryGap: false,
    data: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: '#888', fontSize: 12 }
  },
  yAxis: {
    type: 'value',
    splitLine: { show: false },
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: '#888', fontSize: 12 }
  },
  tooltip: {
    trigger: 'axis',
    backgroundColor: 'rgba(45, 52, 54, 0.95)',
    borderWidth: 0,
    textStyle: { color: '#fff', fontSize: 12 },
    formatter: '{b}: {c}分钟'
  },
  series: [{
    type: 'line',
    smooth: true,
    symbol: 'circle',
    symbolSize: 8,
    lineStyle: { color: '#FF8A3D', width: 3 },
    itemStyle: { color: '#FF8A3D', borderWidth: 2, borderColor: '#fff' },
    areaStyle: {
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: 'rgba(255, 138, 61, 0.4)' },
        { offset: 1, color: 'rgba(255, 138, 61, 0)' }
      ])
    },
    data: [25, 30, 20, 45, 35, 50, 40]
  }]
}

const initChart = () => {
  if (!chartRef.value) return
  if (!desktopChart) {
    desktopChart = echarts.init(chartRef.value)
  }
  desktopChart.setOption(chartOption)
  desktopChart.resize()
}

const initMobileChart = () => {
  const chartEl = Array.isArray(mobileChartRef.value) ? mobileChartRef.value[0] : mobileChartRef.value
  if (!chartEl) return
  if (!mobileChart) {
    mobileChart = echarts.init(chartEl)
  }
  mobileChart.setOption(chartOption)
  mobileChart.resize()
}

const handleResize = () => {
  desktopChart?.resize()
  mobileChart?.resize()
}

const scrollPageToTop = () => {
  window.scrollTo(0, 0)
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0

  if (pageRef.value) {
    pageRef.value.scrollTop = 0
  }
}

onMounted(() => {
  syncMobileTabFromRoute()
  nextTick(() => {
    const token = getAccessToken()
    if(token!=null) {
      getUser()
      getSignSummary()
    }
    initChart()
    initMobileChart()
  })
  window.addEventListener('resize', handleResize)
})

const getUser = async() => {
  try {
    const {id, vipExpireTime, vipTag} = await UserApi.getUser()
    userInfo.value.userId = id
    userInfo.value.vipExpireTime = vipExpireTime
    userInfo.value.vipTag = vipTag
    localStorage.setItem('vipTag', vipTag)
  } catch {
    // 用户信息获取失败时保留默认会员状态
  }
}

watch(
  () => route.query.tab,
  () => {
    syncMobileTabFromRoute()
  }
)

watch(activeMobileTab, async (tab) => {
  await nextTick()
  scrollPageToTop()

  if (tab === 'progress') {
    initMobileChart()
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  window.clearTimeout(checkInToastTimer)
  desktopChart?.dispose()
  mobileChart?.dispose()
})
</script>

<style scoped>
/* 移动端菜单按钮 */
.mobile-menu-btn {
  display: none;
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 1001;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  color: #FF8A3D;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.mobile-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 998;
}

.index-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #FDFBF7 0%, #F5F0E8 50%, #FAF8F5 100%);
  position: relative;
  overflow: hidden;
  color: #2d3436;
  font-family: 'Nunito', 'Source Han Sans CN', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.mobile-shell {
  display: none;
}

.mobile-screen {
  position: relative;
  width: 100%;
  min-height: 100vh;
  padding: 20px 20px 110px;
}

.mobile-bg-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(70px);
  opacity: 0.55;
  pointer-events: none;
}

.mobile-bg-glow-1 {
  width: 220px;
  height: 220px;
  top: 60px;
  left: -80px;
  background: rgba(255, 220, 176, 0.8);
}

.mobile-bg-glow-2 {
  width: 240px;
  height: 240px;
  right: -90px;
  top: 220px;
  background: rgba(214, 236, 255, 0.9);
}

.mobile-topbar {
  display: flex;
  align-items: center;
  gap: 14px;
}

.mobile-search {
  flex: 1;
  min-height: 52px;
  padding: 0 18px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.95);
  box-shadow: 0 12px 32px rgba(140, 104, 68, 0.08);
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-search-icon {
  color: #9a9a9a;
  font-size: 15px;
}

.mobile-search-input {
  flex: 1;
  border: none;
  background: transparent;
  color: #2d3436;
  font-size: 15px;
  outline: none;
}

.mobile-search-input::placeholder {
  color: #aaa;
}

.mobile-avatar-btn {
  width: 52px;
  height: 52px;
  border: none;
  border-radius: 50%;
  padding: 0;
  background: rgba(255, 255, 255, 0.76);
  box-shadow: 0 12px 28px rgba(140, 104, 68, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-avatar-thumb {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f6f1eb 0%, #fff 100%);
  color: #ff8a3d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.mobile-hero {
  margin-top: 28px;
}

.mobile-hero-title {
  font-size: 24px;
  font-weight: 800;
  color: #20252d;
  line-height: 1.2;
}

.mobile-hero-subtitle {
  margin-top: 10px;
  color: #6f7580;
  font-size: 15px;
}

.mobile-stage-list {
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-stage-card {
  position: relative;
  overflow: hidden;
  min-height: clamp(152px, 42vw, 180px);
  padding: 18px 18px 18px 16px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.92);
  box-shadow: 0 18px 40px rgba(133, 110, 86, 0.12);
  display: grid;
  grid-template-columns: 76px 1fr 56px;
  align-items: center;
  gap: 14px;
  cursor: pointer;
}

.mobile-stage-card.has-stage-image {
  grid-template-columns: clamp(191px, 53.6vw, 221px) minmax(0, 1fr);
  align-items: stretch;
  padding-left: 0;
  padding-right: 0;
  gap: 0;
  background:
    linear-gradient(
      90deg,
      color-mix(in srgb, var(--stage-image-bg-start, #fbfaf7) 28%, white) 0%,
      rgba(255, 255, 255, 0.9) 52%,
      rgba(255, 255, 255, 0.96) 100%
    );
}

.mobile-stage-card::before {
  content: '';
  position: absolute;
  right: -18px;
  top: -12px;
  width: 128px;
  height: 128px;
  border-radius: 50%;
  background: radial-gradient(circle, color-mix(in srgb, var(--mobile-stage-color) 20%, white) 0%, rgba(255, 255, 255, 0) 72%);
  opacity: 0.9;
}

.mobile-stage-visual,
.mobile-stage-copy,
.mobile-stage-play {
  position: relative;
  z-index: 1;
}

.mobile-stage-icon {
  width: 76px;
  height: 76px;
  border-radius: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--mobile-stage-color);
  font-size: 34px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.mobile-stage-card.has-stage-image .mobile-stage-visual {
  align-self: stretch;
  display: flex;
  align-items: stretch;
  margin: -18px 0;
  min-height: calc(100% + 36px);
}

.mobile-stage-card.has-stage-image .mobile-stage-icon {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: clamp(152px, 42vw, 180px);
  border-radius: 0;
  background: color-mix(in srgb, var(--stage-image-edge-color, var(--stage-image-bg-start, #fbfaf7)) 14%, white) !important;
  box-shadow: none;
  justify-content: flex-start;
  overflow: hidden;
}

.mobile-stage-image {
  display: block;
  position: absolute;
  inset: 10px;
  width: calc(100% - 20px);
  height: calc(100% - 20px);
  object-fit: contain;
  object-position: center center;
}

.mobile-stage-card.has-stage-image .mobile-stage-copy {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  min-width: 0;
  margin: -18px 0;
  padding: 16px clamp(8px, 2.8vw, 14px) 16px clamp(8px, 2.4vw, 14px);
  border-radius: 0;
  width: 100%;
  height: calc(100% + 36px);
}

.mobile-stage-age {
  display: inline-block;
  color: var(--mobile-stage-color);
  font-size: clamp(14px, 3.9vw, 16px);
  font-weight: 900;
}

.mobile-stage-title {
  margin-top: 6px;
  font-size: clamp(18px, 4.8vw, 22px);
  font-weight: 900;
  color: #252933;
  line-height: 1.25;
  white-space: nowrap;
}

.mobile-stage-desc {
  margin-top: 10px;
  color: #535965;
  font-size: clamp(14px, 3.8vw, 16px);
  line-height: 1.38;
}

.mobile-stage-play {
  width: clamp(40px, 12vw, 48px);
  height: clamp(40px, 12vw, 48px);
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.96);
  color: var(--mobile-stage-color);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.mobile-stage-card.has-stage-image .mobile-stage-play {
  display: none;
}

.mobile-discovery-list {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mobile-discovery-card {
  position: relative;
  overflow: hidden;
  width: 100%;
  min-height: clamp(132px, 36vw, 164px);
  padding: clamp(14px, 4vw, 20px);
  border: 2px solid rgba(255, 255, 255, 0.92);
  border-radius: 24px;
  display: grid;
  grid-template-columns: minmax(88px, 34%) minmax(0, 1fr) 36px;
  align-items: center;
  gap: clamp(9px, 2.4vw, 14px);
  text-align: left;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(133, 110, 86, 0.10);
}

.mobile-discovery-card-video {
  background:
    radial-gradient(circle at 87% 18%, rgba(255, 183, 92, 0.12) 0 24%, transparent 25%),
    linear-gradient(135deg, #fff3e4 0%, #fffaf2 52%, #ffffff 100%);
}

.mobile-discovery-card-flash {
  background:
    radial-gradient(circle at 88% 18%, rgba(149, 123, 244, 0.12) 0 24%, transparent 25%),
    linear-gradient(135deg, #f0eaff 0%, #fbf8ff 55%, #ffffff 100%);
}

.mobile-discovery-card-song {
  background:
    radial-gradient(circle at 88% 18%, rgba(88, 166, 255, 0.13) 0 24%, transparent 25%),
    linear-gradient(135deg, #e8f7ff 0%, #f5fbff 55%, #ffffff 100%);
}

.mobile-discovery-card-book {
  background:
    radial-gradient(circle at 88% 18%, rgba(83, 199, 105, 0.14) 0 24%, transparent 25%),
    linear-gradient(135deg, #effcf1 0%, #f8fff8 55%, #ffffff 100%);
}

.mobile-discovery-card::after {
  content: '';
  position: absolute;
  right: 14px;
  bottom: 10px;
  width: 56px;
  height: 38px;
  opacity: 0.22;
  background-image: radial-gradient(currentColor 1.5px, transparent 1.5px);
  background-size: 12px 12px;
}

.mobile-discovery-card-video::after {
  color: #ff9f43;
}

.mobile-discovery-card-flash::after {
  color: #9b7cf4;
}

.mobile-discovery-card-song::after {
  color: #58a6ff;
}

.mobile-discovery-card-book::after {
  color: #4caf50;
}

.mobile-discovery-visual,
.mobile-discovery-copy,
.mobile-discovery-arrow {
  position: relative;
  z-index: 1;
}

.mobile-discovery-visual {
  min-width: 0;
  height: clamp(88px, 24vw, 116px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-tv-body {
  position: relative;
  width: clamp(82px, 24vw, 112px);
  aspect-ratio: 1.1 / 1;
  border-radius: 22px;
  background: linear-gradient(135deg, #ffd15a 0%, #ff9f2f 100%);
  box-shadow: 0 12px 22px rgba(255, 142, 33, 0.20);
}

.mobile-tv-body::before {
  content: '';
  position: absolute;
  left: 13%;
  right: 13%;
  top: 15%;
  bottom: 20%;
  border-radius: 17px;
  background: #fff8ef;
  box-shadow: inset 0 5px 12px rgba(157, 80, 0, 0.1);
}

.mobile-tv-body::after {
  content: '';
  position: absolute;
  left: 31%;
  right: 31%;
  bottom: 11%;
  height: 5px;
  border-radius: 999px;
  background: rgba(137, 71, 12, 0.42);
}

.mobile-tv-play {
  position: absolute;
  left: 50%;
  top: 47%;
  transform: translate(-50%, -50%);
  width: clamp(34px, 9vw, 44px);
  height: clamp(34px, 9vw, 44px);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: linear-gradient(135deg, #ff9c2a 0%, #ff6f1f 100%);
  box-shadow: 0 8px 14px rgba(255, 111, 31, 0.24);
}

.mobile-discovery-flashcards {
  perspective: 420px;
}

.mobile-flashcard {
  position: absolute;
  width: clamp(58px, 17vw, 82px);
  height: clamp(78px, 23vw, 108px);
  border-radius: 16px;
  box-shadow: 0 10px 22px rgba(113, 82, 214, 0.14);
}

.mobile-flashcard-back {
  transform: rotate(-15deg) translate(-20px, 9px);
  background: linear-gradient(160deg, #ffcb5b 0%, #ff7f65 100%);
}

.mobile-flashcard-mid {
  transform: rotate(12deg) translate(20px, 2px);
  background: linear-gradient(160deg, #8ed8ff 0%, #618bf6 100%);
}

.mobile-flashcard-front {
  transform: rotate(2deg);
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8d69ee;
  font-size: clamp(25px, 7.8vw, 36px);
  font-weight: 900;
  text-shadow: 0 5px 12px rgba(117, 78, 213, 0.2);
}

.mobile-music-body,
.mobile-book-body {
  position: relative;
  width: clamp(82px, 24vw, 112px);
  aspect-ratio: 1.1 / 1;
  border-radius: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 12px 22px rgba(85, 138, 201, 0.18);
}

.mobile-music-body {
  background: linear-gradient(135deg, #7fd5ff 0%, #4d8cf7 100%);
}

.mobile-book-body {
  background: linear-gradient(135deg, #8ee783 0%, #4ccc60 100%);
  box-shadow: 0 16px 30px rgba(68, 184, 85, 0.2);
}

.mobile-music-body::before,
.mobile-book-body::before {
  content: '';
  position: absolute;
  left: 13%;
  right: 13%;
  top: 15%;
  bottom: 20%;
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.94);
}

.mobile-music-body::after,
.mobile-book-body::after {
  content: '';
  position: absolute;
  left: 31%;
  right: 31%;
  bottom: 11%;
  height: 5px;
  border-radius: 999px;
}

.mobile-music-body::after {
  background: rgba(48, 119, 221, 0.32);
}

.mobile-book-body::after {
  background: rgba(33, 151, 65, 0.32);
}

.mobile-music-note,
.mobile-book-headphones,
.mobile-book-page {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.mobile-music-note {
  color: #5c8cff;
  font-size: clamp(30px, 9vw, 44px);
}

.mobile-book-headphones {
  color: #3bc66a;
  font-size: clamp(34px, 10vw, 48px);
}

.mobile-book-page {
  position: absolute;
  color: #ff9a3e;
  font-size: clamp(18px, 5.4vw, 25px);
}

.mobile-discovery-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.mobile-discovery-title {
  color: #252933;
  font-size: clamp(16px, 4.4vw, 20px);
  line-height: 1.1;
  font-weight: 900;
  white-space: nowrap;
  word-break: keep-all;
}

.mobile-discovery-desc {
  margin-top: 7px;
  color: #8b8f99;
  font-size: clamp(11px, 3vw, 13px);
  line-height: 1.3;
  font-weight: 700;
}

.mobile-discovery-action {
  margin-top: 10px;
  min-height: 30px;
  padding: 0 13px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.68);
  font-size: clamp(11px, 3vw, 13px);
  line-height: 1;
  font-weight: 900;
}

.mobile-discovery-card-video .mobile-discovery-action {
  color: #ff7f24;
  background: rgba(255, 207, 150, 0.46);
}

.mobile-discovery-card-flash .mobile-discovery-action {
  color: #9072ee;
  background: rgba(157, 128, 244, 0.18);
}

.mobile-discovery-card-song .mobile-discovery-action {
  color: #338de8;
  background: rgba(51, 141, 232, 0.14);
}

.mobile-discovery-card-book .mobile-discovery-action {
  color: #36a852;
  background: rgba(54, 168, 82, 0.14);
}

.mobile-discovery-arrow {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: end;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 8px 16px rgba(73, 55, 33, 0.09);
  font-size: 16px;
}

.mobile-discovery-card-video .mobile-discovery-arrow {
  color: #ff8428;
}

.mobile-discovery-card-flash .mobile-discovery-arrow {
  color: #8c6fed;
}

.mobile-discovery-card-song .mobile-discovery-arrow {
  color: #338de8;
}

.mobile-discovery-card-book .mobile-discovery-arrow {
  color: #36a852;
}

.mobile-progress-head {
  padding-top: 8px;
}

.mobile-section-heading {
  font-size: 28px;
  font-weight: 800;
  color: #20252d;
}

.mobile-panel-card {
  margin-top: 18px;
  padding: 20px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.94);
  box-shadow: 0 18px 40px rgba(133, 110, 86, 0.11);
}

.mobile-panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.mobile-panel-head-chart {
  margin-bottom: 8px;
}

.mobile-panel-title {
  color: #20252d;
  font-size: 17px;
  font-weight: 800;
}

.mobile-panel-badge {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 170, 102, 0.16);
  color: #da9954;
  font-size: 13px;
  font-weight: 700;
}

.mobile-panel-meta {
  color: #8d9098;
  font-size: 13px;
  font-weight: 600;
}

.mobile-calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px 4px;
}

.mobile-calendar-weekday {
  text-align: center;
  color: #8b8f97;
  font-size: 13px;
  font-weight: 700;
}

.mobile-calendar-day {
  min-height: 40px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: #59606c;
}

.mobile-calendar-day.is-empty {
  visibility: hidden;
}

.mobile-calendar-number {
  font-size: 14px;
  font-weight: 700;
}

.mobile-calendar-star {
  color: #f4ba3c;
  font-size: 13px;
}

.mobile-calendar-day.is-today {
  background: rgba(92, 202, 120, 0.16);
}

.mobile-calendar-day.is-today .mobile-calendar-number {
  color: #23af56;
}

.mobile-check-in-btn {
  width: 100%;
  min-height: 46px;
  margin-top: 18px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff8b30 0%, #ff711b 100%);
  color: #ffffff;
  font-size: 15px;
  font-weight: 800;
  box-shadow: 0 12px 24px rgba(255, 123, 33, 0.2);
}

.mobile-check-in-btn.checked {
  background: rgba(90, 198, 116, 0.16);
  color: #28a552;
  box-shadow: none;
}

.mobile-check-in-btn:disabled {
  cursor: default;
}

.mobile-chart-container {
  width: 100%;
  height: 220px;
}

.mobile-vocab-card {
  position: relative;
  overflow: hidden;
}

.mobile-vocab-metric + .mobile-vocab-metric {
  margin-top: 22px;
}

.mobile-vocab-number {
  margin-top: 20px;
  font-size: 56px;
  line-height: 1;
  font-weight: 800;
  color: #20252d;
}

.mobile-vocab-deco {
  position: absolute;
  opacity: 0.9;
}

.mobile-vocab-deco-left {
  left: 16px;
  bottom: 18px;
  color: #9cb9ff;
  font-size: 34px;
}

.mobile-vocab-deco-top {
  right: 20px;
  top: 62px;
  color: #f6b59a;
  font-size: 30px;
  transform: rotate(-12deg);
}

.mobile-vocab-deco-right {
  right: 18px;
  top: 18px;
  color: #ffc37a;
  font-size: 34px;
}

.mobile-vocab-deco-bottom {
  right: 24px;
  bottom: 12px;
  color: #7bc45d;
  font-size: 36px;
}

.mobile-profile-top {
  padding-top: 10px;
  text-align: center;
}

.mobile-profile-avatar {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto;
}

.mobile-profile-avatar-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  border: 4px solid rgba(255, 255, 255, 0.96);
  box-shadow: 0 18px 40px rgba(133, 110, 86, 0.12);
  color: #ff8a3d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 38px;
}

.mobile-profile-name {
  margin-top: 18px;
  color: #20252d;
  font-size: 22px;
  font-weight: 800;
}

.mobile-membership-card {
  margin-top: 14px;
  padding: 16px 18px;
  border-radius: 18px;
  background: linear-gradient(90deg, #f9d4bc 0%, #f8e9dd 100%);
  box-shadow: 0 16px 36px rgba(217, 157, 112, 0.16);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  text-align: left;
}

.mobile-membership-copy {
  min-width: 0;
}

.mobile-membership-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #9f5f31;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.1;
}

.mobile-membership-crown {
  font-size: 15px;
  color: #b56b37;
}

.mobile-membership-expiry {
  margin-top: 8px;
  color: rgba(159, 95, 49, 0.72);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}

.mobile-membership-expiry.expired {
  color: #d85d48;
  text-align: center;
}

.mobile-membership-link {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 0;
  color: #9f5f31;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}

.mobile-profile-menu {
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-profile-item {
  width: 100%;
  min-height: 76px;
  border: 1px solid rgba(255, 255, 255, 0.94);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 18px 40px rgba(133, 110, 86, 0.1);
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 14px;
  text-align: left;
}

.mobile-profile-item-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--profile-accent) 16%, white);
  color: var(--profile-accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.mobile-profile-item-label {
  flex: 1;
  color: #20252d;
  font-size: 17px;
  font-weight: 700;
}

.mobile-profile-item-arrow {
  color: #8e939c;
  font-size: 14px;
}

.mobile-tabbar {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  height: 84px;
  padding: 10px 8px 12px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid rgba(255, 255, 255, 0.96);
  box-shadow: 0 22px 44px rgba(133, 110, 86, 0.18);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  z-index: 20;
}

.mobile-tabbar-item {
  border: none;
  background: transparent;
  color: #8d9199;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
}

.mobile-tabbar-icon {
  font-size: 20px;
}

.mobile-tabbar-item.active {
  color: #ff8a3d;
}

.bg-decoration { position: absolute; inset: 0; overflow: hidden; pointer-events: none; }
.orb { position: absolute; border-radius: 50%; filter: blur(80px); opacity: 0.5; animation: float-orb 8s ease-in-out infinite; }
.orb-1 { width: 400px; height: 400px; background: linear-gradient(135deg, #FFE4D4 0%, #FFD4C4 100%); top: -100px; right: -100px; animation-delay: 0s; }
.orb-2 { width: 350px; height: 350px; background: linear-gradient(135deg, #E8F4FF 0%, #D4E8FF 100%); bottom: -50px; left: -100px; animation-delay: -3s; }
.orb-3 { width: 250px; height: 250px; background: linear-gradient(135deg, #FFEAA8 0%, #FFD88A 100%); top: 50%; left: 30%; animation-delay: -5s; }

@keyframes float-orb {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -30px) scale(1.05); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
}

.glass-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
}

.content-wrapper {
  display: flex;
  height: 100vh;
  position: relative;
  z-index: 10;
}

/* 左侧导航 */
.sidebar { 
  width: 264px; 
  flex-shrink: 0; 
  height: 100vh;
  overflow: hidden;
  background: #FFF9F5;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);
  position: relative;
}
.nav-card { 
  height: 100%;
  min-height: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 20px;
  border-bottom: 1px solid rgba(255, 138, 61, 0.15);
}
.nav-logo .logo-icon {
  width: 62px; height: 62px;
  background: #fff;
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(140, 104, 68, 0.12);
}

.nav-logo .logo2-icon {
  display: block;
  width: 52px;
  height: 52px;
  object-fit: contain;
}

.brand-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.nav-logo .brand-name {
  margin: 0;
  font-size: 24px; font-weight: 800; color: #2D3436; letter-spacing: 1px;
  line-height: 1.05;
}

.brand-subtitle {
  color: #a78b76;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.2px;
  line-height: 1;
}

.nav-list { 
  list-style: none; 
  display: flex; 
  flex-direction: column; 
  flex-shrink: 0;
  gap: clamp(12px, 2vh, 20px); 
  padding: clamp(12px, 2vh, 16px) 12px;
}
.nav-item {
  display: flex; align-items: center; gap: 14px;
  padding: clamp(12px, 1.8vh, 18px) 18px; border-radius: 14px;
  color: #666; font-size: 15px; font-weight: 500;
  transition: all 0.3s ease; cursor: pointer;
  position: relative;
  text-decoration: none;
}
.nav-item:hover { 
  background: rgba(255, 138, 61, 0.08); 
  color: #FF8A3D;
}
.nav-item.active { 
  background: linear-gradient(135deg, #FFE4D4 0%, #FFD4C4 100%); 
  color: #FF6B35; 
  font-weight: 700;
  box-shadow: 0 4px 16px rgba(255, 138, 61, 0.2);
}
.nav-icon { font-size: 18px; width: 24px; text-align: center; }

.nav-menu-visual {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  margin-top: auto;
  border-radius: 0;
  overflow: hidden;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.nav-menu-image {
  display: block;
  flex-shrink: 0;
  width: 100%;
  min-width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center bottom;
}

/* 右侧内容 */
.right-wrapper { 
  flex: 1; 
  display: flex; 
  flex-direction: column; 
  padding: 8px 16px 16px;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.welcome-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 28px 12px;
  margin-bottom: 10px;
  flex-shrink: 0;
}
.welcome-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.welcome-text { font-size: 22px; font-weight: 700; color: #2D3436; }
.wave { display: inline-block; animation: wave 1s ease-in-out infinite; }
@keyframes wave { 0%, 100% { transform: rotate(0); } 25% { transform: rotate(20deg); } 75% { transform: rotate(-20deg); } }
.date-text { font-size: 14px; color: #888; margin-top: 4px; }

.welcome-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 14px;
}

.top-membership-card {
  min-width: 272px;
  flex-shrink: 0;
  padding: 10px 12px 10px 18px;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255, 248, 243, 0.96) 0%, rgba(255, 244, 236, 0.92) 100%);
  border: 1px solid rgba(255, 151, 83, 0.28);
  box-shadow:
    0 10px 24px rgba(209, 145, 93, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.top-account-badge {
  flex-shrink: 0;
  width: 74px;
  padding: 0;
  border: none;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
}

.top-account-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fff5e8 0%, #ffd8ad 100%);
  color: #d97721;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  box-shadow:
    0 8px 16px rgba(214, 126, 45, 0.18),
    inset 0 0 0 1px rgba(255, 255, 255, 0.8);
}

.top-account-label {
  color: #9f5f31;
  font-size: 13px;
  font-weight: 900;
  line-height: 1;
  white-space: nowrap;
}

.top-membership-copy {
  min-width: 0;
}

.top-membership-title {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #db7420;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.1;
}

.top-membership-crown {
  color: #f0a63b;
  font-size: 14px;
}

.top-membership-expiry {
  margin-top: 6px;
  color: #a08471;
  font-size: 12px;
  line-height: 1.2;
  font-weight: 600;
}

.top-membership-expiry.expired {
  color: #d85d48;
  text-align: center;
}

.top-membership-btn {
  min-width: 126px;
  height: 48px;
  padding: 0 22px;
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff8b2f 0%, #ff6d16 100%);
  color: #ffffff;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 18px rgba(255, 122, 24, 0.26);
  transition: transform 0.25s ease, box-shadow 0.25s ease, filter 0.25s ease;
}

.top-membership-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 22px rgba(255, 122, 24, 0.3);
  filter: saturate(1.04);
}

.top-membership-btn:active {
  transform: scale(0.98);
}

/* 内容区域 */
.content-area {
  flex: 1;
  display: flex;
  gap: 20px;
  min-height: 0;
  overflow: hidden;
}

.left-content {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.right-panel {
  flex: 0 1 clamp(260px, 19vw, 330px);
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: calc(22px * 1.2 + 10px);
  box-sizing: border-box;
  min-height: 0;
  overflow: hidden;
}

.quick-entry-list {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.quick-entry-card {
  width: 100%;
  min-height: clamp(72px, 9.2vh, 88px);
  padding: clamp(12px, 1.2vw, 16px) clamp(14px, 1.4vw, 18px);
  border: 0;
  display: flex;
  align-items: center;
  gap: clamp(12px, 1vw, 16px);
  text-align: left;
  cursor: pointer;
  box-shadow: none;
  transition: transform 0.22s ease, background-color 0.22s ease, box-shadow 0.22s ease;
}

.quick-entry-card:hover {
  transform: translateY(-2px);
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 12px 28px rgba(45, 52, 54, 0.08);
}

.quick-entry-card:active {
  transform: translateY(0);
}

.quick-entry-icon {
  flex: 0 0 auto;
  width: clamp(42px, 3.6vw, 52px);
  height: clamp(42px, 3.6vw, 52px);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(17px, 1.45vw, 21px);
}

.quick-entry-icon-video {
  color: #ffffff;
  background: linear-gradient(135deg, #ffad5b 0%, #ff8a3d 100%);
  box-shadow: 0 10px 22px rgba(255, 138, 61, 0.22);
}

.quick-entry-icon-flash {
  color: #ffffff;
  background: linear-gradient(135deg, #a991f7 0%, #7c6be8 100%);
  box-shadow: 0 10px 22px rgba(124, 107, 232, 0.2);
}

.quick-entry-icon-song {
  color: #ffffff;
  background: linear-gradient(135deg, #7fd5ff 0%, #4d8cf7 100%);
  box-shadow: 0 10px 22px rgba(85, 138, 201, 0.2);
}

.quick-entry-icon-book {
  color: #ffffff;
  background: linear-gradient(135deg, #8ee783 0%, #4ccc60 100%);
  box-shadow: 0 10px 22px rgba(68, 184, 85, 0.2);
}

.quick-entry-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.quick-entry-title {
  font-size: clamp(15px, 1.18vw, 18px);
  line-height: 1.2;
  color: #2D3436;
  font-weight: 800;
}

.quick-entry-desc {
  font-size: clamp(12px, 0.95vw, 14px);
  line-height: 1.3;
  color: #999;
  font-weight: 600;
}

.section-card {
  flex: 1 1 auto;
  min-height: 0;
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  overflow: visible;
}
.section-title { font-size: 22px; line-height: 1.2; font-weight: 700; color: #2D3436; margin-bottom: 10px; }

.stage-grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 14px;
  overflow: visible;
}
.stage-card {
  position: relative;
  display: flex; align-items: flex-start; gap: 16px;
  min-height: 0;
  padding: clamp(16px, 2vh, 24px) 20px; border-radius: 20px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 10px 24px rgba(196, 146, 92, 0.08);
  transition: all 0.3s ease; cursor: pointer;
  isolation: isolate;
}
.stage-card.has-stage-image {
  align-items: stretch;
  overflow: hidden;
  padding: 0;
  gap: 0;
  background-image:
    linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.02) 0%,
      rgba(255, 255, 255, 0.22) 42%,
      rgba(255, 255, 255, 0.86) 62%,
      rgba(255, 255, 255, 0.94) 100%
    ),
    var(--stage-card-image);
  background-size: 100% 100%, auto 100%;
  background-position: center center, left center;
  background-repeat: no-repeat;
}
.stage-card::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease, box-shadow 0.3s ease;
}
.stage-card:hover {
  transform: translateY(-4px);
}
.stage-card:hover::after {
  opacity: 1;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.86),
    0 14px 30px rgba(255, 138, 61, 0.10),
    0 0 34px rgba(255, 138, 61, 0.16);
}
.stage-icon {
  width: 52px; height: 52px; border-radius: 16px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; color: var(--stage-color);
}
.stage-card.has-stage-image .stage-icon {
  display: none;
}
.stage-image {
  display: block;
  position: static;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
}
.stage-card.has-stage-image .stage-info {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  min-width: 0;
  width: 100%;
  flex: 1;
  margin: 0;
  padding:
    clamp(14px, 2.4vh, 28px)
    clamp(16px, 1.6vw, 22px)
    clamp(14px, 2.4vh, 28px)
    clamp(59%, 61vw, 63%);
  border-radius: 0;
}
.stage-title {
  font-size: clamp(17px, min(1.7vw, 2.2vh), 24px);
  line-height: 1.14;
  font-weight: 800;
  color: #2D3436;
  word-break: keep-all;
}
.stage-desc {
  font-size: clamp(12px, min(1.2vw, 1.65vh), 17px);
  line-height: 1.38;
  color: #888;
  margin-top: clamp(5px, 1vh, 8px);
}
.stage-tag {
  display: inline-block;
  margin-top: clamp(6px, 1.2vh, 10px);
  padding: clamp(4px, 0.8vh, 6px) clamp(10px, 1.1vw, 14px);
  border-radius: 50px;
  font-size: clamp(12px, min(1.1vw, 1.55vh), 16px);
  line-height: 1.15;
  font-weight: 700;
}

.data-grid {
  flex: 0 0 clamp(180px, 25vh, 260px);
  min-height: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.data-card, .chart-card {
  min-height: 0;
  padding: clamp(18px, 2.4vh, 24px);
  overflow: hidden;
}
.data-title {
  font-size: clamp(12px, min(1vw, 1.55vh), 14px);
  line-height: 1.35;
  color: #000000;
  font-weight: 500;
  margin-bottom: clamp(8px, 1.3vh, 12px);
}
.data-content {
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}
.data-metrics {
  display: flex;
  flex-direction: column;
  gap: clamp(12px, 2.2vh, 22px);
}
.number {
  font-size: clamp(28px, min(3.2vw, 4.6vh), 42px);
  line-height: 1;
  font-weight: 800;
  color: #2D3436;
}
.growth {
  display: block;
  font-size: clamp(11px, min(0.95vw, 1.4vh), 13px);
  color: #4CAF50;
  font-weight: 600;
  margin-top: 4px;
}
.data-deco { font-size: clamp(34px, min(4vw, 5.6vh), 52px); color: #FF8A3D; opacity: 0.15; }
.chart-container { width: 100%; height: clamp(112px, 15vh, 166px); }

.calendar-card {
  flex: 0 1 clamp(300px, 43vh, 360px);
  width: 100%;
  min-height: 0;
  max-height: clamp(300px, 43vh, 360px);
  padding: clamp(14px, 1.2vw, 20px);
  box-shadow: none;
  display: flex;
  flex-direction: column;
}
.calendar-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: clamp(8px, 1.2vh, 12px); }
.calendar-title { font-size: clamp(16px, 1.3vw, 20px); font-weight: 800; color: #2D3436; }
.calendar-month { font-size: clamp(13px, 1vw, 15px); color: #FF8A3D; font-weight: 700; }
.calendar-grid {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(7, minmax(0, 1fr));
  row-gap: clamp(9px, 1.05vw, 14px);
  column-gap: clamp(3px, 0.45vw, 6px);
  text-align: center;
  align-content: stretch;
}
.calendar-weekday {
  font-size: clamp(12px, 0.95vw, 14px);
  color: #AAA;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.calendar-day {
  font-size: clamp(13px, 1vw, 15px); color: #666; padding: 0; border-radius: 12px;
  transition: all 0.2s; cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.calendar-day:hover:not(.is-empty) { background: rgba(0, 0, 0, 0.05); }
.calendar-day.is-today { background: #FF8A3D; color: white; font-weight: 700; }
.calendar-day.is-checked { background: rgba(76, 175, 80, 0.15); color: #4CAF50; }
.calendar-day.is-empty { cursor: default; }
.streak-tip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(8px, 1vw, 14px);
  margin-top: clamp(4px, 0.7vh, 6px);
  margin-inline: -10px;
  padding: clamp(4px, 0.55vw, 7px) clamp(10px, 1.2vw, 14px);
  background: rgba(76, 175, 80, 0.1);
  border-radius: 12px;
}
.streak-main { display: flex; align-items: center; gap: 8px; min-width: 0; }
.streak-icon { font-size: 20px; color: #FF8A3D; }
.streak-count { display: block; font-size: 14px; line-height: 1.1; font-weight: 800; color: #4CAF50; }
.streak-hint { font-size: 11px; line-height: 1.1; color: #888; }

.check-in-btn {
  flex-shrink: 0;
  min-width: clamp(76px, 6.2vw, 96px);
  min-height: clamp(28px, 2vw, 32px);
  padding: 0 clamp(12px, 1.1vw, 16px);
  border: none;
  border-radius: 999px;
  background: linear-gradient(135deg, #ff8b30 0%, #ff711b 100%);
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 10px 18px rgba(255, 123, 33, 0.18);
  transition: transform 0.22s ease, box-shadow 0.22s ease, opacity 0.22s ease;
}

.check-in-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 22px rgba(255, 123, 33, 0.24);
}

.check-in-btn.checked {
  background: rgba(90, 198, 116, 0.16);
  color: #28a552;
  box-shadow: none;
}

.check-in-btn:disabled {
  cursor: default;
  transform: none;
}

.checkin-toast {
  position: fixed;
  left: 50%;
  bottom: 32px;
  transform: translateX(-50%);
  z-index: 2200;
  max-width: min(88vw, 420px);
  padding: 12px 18px;
  border-radius: 999px;
  background: rgba(45, 52, 54, 0.92);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.checkin-toast-enter-active,
.checkin-toast-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}

.checkin-toast-enter-from,
.checkin-toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 10px);
}

.tree-card {
  padding: 20px;
  text-align: center;
  box-shadow: none;
}

/* 顶部区域 */
.tree-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}
.tree-title { font-size: 16px; font-weight: 700; color: #2D3436; }
.level-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.level-badge {
  padding: 4px 12px;
  background: linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%);
  color: white;
  font-size: 12px;
  font-weight: 700;
  border-radius: 50px;
}
.level-name {
  font-size: 11px;
  color: #999;
}

/* 中间展示区 */
.tree-showcase {
  position: relative;
  height: 120px;
  margin-bottom: 12px;
}

.particles { position: absolute; inset: 0; }
.particle {
  position: absolute;
  width: 6px;
  height: 6px;
  background: #FFD700;
  border-radius: 50%;
  animation: sparkle 2s ease-in-out infinite;
}
.particle.p1 { top: 20%; left: 15%; animation-delay: 0s; }
.particle.p2 { top: 30%; right: 20%; animation-delay: 0.5s; }
.particle.p3 { top: 50%; left: 10%; animation-delay: 1s; }
.particle.p4 { top: 40%; right: 15%; animation-delay: 1.5s; }
@keyframes sparkle {
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.5); }
}

.tree-illustration {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
}

.tree-plant {
  position: relative;
  width: 50px;
  height: 70px;
  animation: sway 3s ease-in-out infinite;
}
@keyframes sway {
  0%, 100% { transform: rotate(-2deg); }
  50% { transform: rotate(2deg); }
}

/* 树苗茎干 */
.tree-stem {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 5px;
  height: 40px;
  background: linear-gradient(to top, #6D4C41, #81C784);
  border-radius: 3px;
}

/* 顶部嫩芽 */
.tree-bud {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 10px;
  height: 14px;
  background: linear-gradient(to top, #66BB6A, #81C784);
  border-radius: 50% 50% 40% 40%;
}

/* 左侧叶子 */
.leaf-left {
  position: absolute;
  top: 15px;
  left: -8px;
  width: 22px;
  height: 14px;
  background: linear-gradient(135deg, #81C784 0%, #66BB6A 100%);
  border-radius: 50% 0 50% 50%;
  transform: rotate(-20deg);
}

/* 右侧叶子 */
.leaf-right {
  position: absolute;
  top: 20px;
  right: -8px;
  width: 22px;
  height: 14px;
  background: linear-gradient(135deg, #66BB6A 0%, #81C784 100%);
  border-radius: 0 50% 50% 50%;
  transform: rotate(20deg);
}

/* 中间叶子 */
.leaf-center {
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 18px;
  height: 12px;
  background: linear-gradient(to top, #66BB6A, #A5D6A7);
  border-radius: 50% 50% 50% 50%;
}

/* 泥土 */
.ground {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 32px;
  height: 12px;
  background: linear-gradient(to bottom, #5D4037, #4E342E);
  border-radius: 50%;
}

/* 文案说明 */
.tree-slogan {
  font-size: 13px;
  color: #999;
  margin-bottom: 16px;
}

/* 成长进度 */
.progress-section { margin-bottom: 16px; }
.progress-header { display: flex; justify-content: space-between; margin-bottom: 8px; }
.progress-label { font-size: 13px; color: #666; }
.progress-value { font-size: 13px; font-weight: 700; color: #4CAF50; }
.progress-bar { height: 8px; background: #F0F0F0; border-radius: 4px; overflow: hidden; }
.progress-fill { height: 100%; background: linear-gradient(90deg, #4CAF50 0%, #66BB6A 100%); border-radius: 4px; transition: width 1s ease; animation: progressGrow 1.5s ease-out; }
@keyframes progressGrow {
  from { width: 0 !important; }
}
.learn-btn {
  width: 100%; padding: 14px;
  background: linear-gradient(135deg, #FF8A3D 0%, #FF6B35 100%);
  border: none; border-radius: 24px; color: white;
  font-size: 15px; font-weight: 700; cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(255, 138, 61, 0.3);
  position: relative;
  overflow: hidden;
}
.learn-btn:hover { transform: scale(1.02); box-shadow: 0 6px 20px rgba(255, 138, 61, 0.4); }
.learn-btn:active { transform: scale(0.98); }

.learn-btn.watering {
  pointer-events: none;
}

.watering-animation {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
}

.watering-text {
  animation: pulse 0.6s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* 浇水动画层 */
.watering-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.watering-scene {
  position: relative;
  width: 100%;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tree-grow {
  animation: treeShake 0.5s ease-in-out infinite;
}

@keyframes treeShake {
  0%, 100% { transform: rotate(-2deg); }
  50% { transform: rotate(2deg); }
}

/* 小树样式 */
.mini-tree {
  position: relative;
  width: 60px;
  height: 80px;
}

.mini-crown {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  height: 45px;
  background: radial-gradient(ellipse at center, #81C784 0%, #66BB6A 60%, #4CAF50 100%);
  border-radius: 50% 50% 45% 45%;
  box-shadow: inset -5px -5px 10px rgba(0,0,0,0.1), inset 3px 3px 8px rgba(255,255,255,0.3);
}

.mini-crown::before {
  content: '';
  position: absolute;
  top: 6px;
  left: 8px;
  width: 12px;
  height: 10px;
  background: rgba(255,255,255,0.3);
  border-radius: 50%;
}

.mini-trunk {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 8px;
  height: 30px;
  background: linear-gradient(to right, #6D4C41 0%, #8D6E63 50%, #6D4C41 100%);
  border-radius: 2px;
}

.mini-ground {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 10px;
  background: linear-gradient(to bottom, #5D4037, #4E342E);
  border-radius: 50%;
}

/* 小水壶 */
.mini-watering-can {
  position: absolute;
  right: 10px;
  top: 25px;
  animation: pourWater 0.4s ease-in-out infinite alternate;
}

@keyframes pourWater {
  0% { transform: rotate(-12deg); }
  100% { transform: rotate(-20deg); }
}

.mini-can-body {
  width: 32px;
  height: 28px;
  background: linear-gradient(135deg, #4FC3F7 0%, #29B6F6 50%, #03A9F4 100%);
  border-radius: 4px 4px 8px 8px;
  position: relative;
  box-shadow: inset -2px -2px 4px rgba(0,0,0,0.15), inset 2px 2px 4px rgba(255,255,255,0.3);
}

.mini-can-body::before {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 6px;
  height: 16px;
  background: rgba(255,255,255,0.35);
  border-radius: 3px;
}

/* 壶嘴 */
.mini-can-spout {
  position: absolute;
  top: 6px;
  left: -14px;
  width: 16px;
  height: 7px;
  background: linear-gradient(135deg, #4FC3F7, #29B6F6);
  border-radius: 4px 0 0 4px;
  transform: rotate(-30deg);
}

/* 壶嘴出水口 */
.mini-can-spout::after {
  content: '';
  position: absolute;
  left: -3px;
  top: -1px;
  width: 6px;
  height: 9px;
  background: linear-gradient(135deg, #4FC3F7, #29B6F6);
  border-radius: 2px;
  transform: rotate(5deg);
}

/* 手柄 */
.mini-can-handle {
  position: absolute;
  top: 2px;
  right: -8px;
  width: 10px;
  height: 20px;
  border: 3px solid #29B6F6;
  border-left: none;
  border-radius: 0 6px 6px 0;
  background: transparent;
}

/* 水流 - 从壶嘴流出 */
.mini-water-flow {
  position: absolute;
  top: 18px;
  left: -20px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  animation: waterFlow 0.4s linear infinite;
}

@keyframes waterFlow {
  0% { opacity: 1; transform: translateY(0) rotate(-30deg); }
  100% { opacity: 0; transform: translateY(25px) rotate(-30deg); }
}

.mini-drop {
  width: 5px;
  height: 10px;
  background: linear-gradient(180deg, #81D4FA, #4FC3F7);
  border-radius: 50% 50% 50% 50%;
  animation: dropFall 0.6s ease-in infinite;
}

.mini-drop:nth-child(1) { animation-delay: 0s; }
.mini-drop:nth-child(2) { animation-delay: 0.15s; }
.mini-drop:nth-child(3) { animation-delay: 0.3s; }

@keyframes dropFall {
  0% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.5); }
}

/* 响应式 */
@media (max-width: 1200px) {
  .sidebar { width: 224px; }
  .right-panel { flex-basis: clamp(250px, 23vw, 300px); }
  .stage-card.has-stage-image .stage-info {
    padding-left: clamp(52%, 54vw, 56%);
  }
}
@media (max-width: 992px) {
  .sidebar { width: 208px; }
  .content-wrapper { gap: 16px; }
  .right-panel { 
    width: 100%; 
    flex-direction: row;
    overflow-x: auto;
    gap: 12px;
    padding-bottom: 16px;
  }
  .right-panel > * { min-width: min(320px, 100%); flex-shrink: 0; }
}
@media (max-width: 768px), (min-width: 769px) and (max-width: 1024px) and (orientation: portrait) {
  .mobile-shell {
    display: block;
    position: relative;
    min-height: 100vh;
    z-index: 12;
  }

  .content-wrapper,
  .mobile-menu-btn,
  .mobile-overlay {
    display: none !important;
  }

  .bg-decoration {
    opacity: 0.5;
  }

  .mobile-screen {
    min-height: 100svh;
    padding: 14px 14px calc(env(safe-area-inset-bottom, 0px) + 86px);
  }

  .mobile-search {
    min-height: 46px;
    padding: 0 16px;
  }

  .mobile-avatar-btn {
    width: 46px;
    height: 46px;
  }

  .mobile-avatar-thumb {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .mobile-hero {
    margin-top: 14px;
  }

  .mobile-hero-title {
    font-size: 21px;
    line-height: 1.15;
  }

  .mobile-hero-subtitle {
    margin-top: 5px;
    font-size: 13px;
    line-height: 1.35;
  }

  .mobile-stage-list {
    margin-top: 14px;
    gap: 10px;
  }

  .mobile-stage-card {
    min-height: 0;
    height: auto;
    aspect-ratio: 2.65 / 1;
    padding: 12px 14px 12px 12px;
    border-radius: 20px;
  }

  .mobile-stage-card.has-stage-image {
    grid-template-columns: minmax(0, 1fr);
    background-color: transparent;
    background-image: var(--mobile-stage-image);
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
  }

  .mobile-stage-card.has-stage-image .mobile-stage-visual {
    display: none;
  }

  .mobile-stage-card.has-stage-image .mobile-stage-copy {
    margin: 0;
    height: 100%;
    padding: 10px 12px 10px 49%;
    background: transparent;
  }

  .mobile-stage-age {
    font-size: clamp(11px, 3.2vw, 13px);
  }

  .mobile-stage-title {
    margin-top: 3px;
    font-size: clamp(15px, 4.1vw, 18px);
    line-height: 1.14;
  }

  .mobile-stage-desc {
    margin-top: 5px;
    font-size: clamp(11px, 3.15vw, 13px);
    line-height: 1.28;
  }

  .mobile-discovery-card {
    grid-template-columns: minmax(76px, 32%) minmax(0, 1fr) 30px;
    min-height: 124px;
    padding: 12px;
    border-radius: 22px;
  }

  .mobile-discovery-action {
    min-height: 28px;
    padding: 0 11px;
  }

  .mobile-discovery-arrow {
    width: 30px;
    height: 30px;
    font-size: 14px;
  }

  .mobile-tabbar {
    left: 14px;
    right: 14px;
    bottom: calc(env(safe-area-inset-bottom, 0px) + 10px);
    height: 70px;
    padding: 8px 8px 10px;
    border-radius: 22px;
  }

  .mobile-tabbar-item {
    gap: 5px;
    font-size: 12px;
  }

  .mobile-tabbar-icon {
    font-size: 18px;
  }

  .index-page {
    overflow-y: auto;
    padding-bottom: 12px;
  }
  
  .sidebar {
    position: fixed;
    left: -280px;
    top: 0;
    z-index: 999;
    transition: left 0.3s ease;
    width: 280px;
  }
  .sidebar.mobile-open { left: 0; }
  
  .content-wrapper { 
    flex-direction: column;
    height: auto;
    min-height: 100vh;
    gap: 0;
  }
  
  .right-wrapper { 
    padding: 70px 16px 24px; 
    width: 100%;
    min-height: 0;
  }
  
  .welcome-bar { 
    flex-direction: column; 
    align-items: center;
    gap: 16px; 
    text-align: center;
    margin-bottom: 16px;
    flex-shrink: 0;
  }
  .welcome-left { display: flex; flex-direction: column; align-items: center; }
  .welcome-text { font-size: 18px; }
  .content-area { 
    flex-direction: column;
    width: 100%;
    gap: 20px;
  }
  .left-content { width: 100%; }
  
  .right-panel { 
    width: 100%; 
    flex-direction: column;
    gap: 16px;
    padding-top: 0;
  }
  
  .stage-grid { grid-template-columns: 1fr; }
  .data-grid { grid-template-columns: 1fr; }
  
  .nav-logo { padding: 16px; gap: 12px; }
  .nav-logo .logo-icon { width: 58px; height: 58px; font-size: 20px; }
  .nav-logo .logo2-icon { width: 48px; height: 48px; }
  .nav-logo .brand-name { font-size: 22px; }
  .nav-logo .brand-subtitle { font-size: 13px; }
  
  .index-page { overflow-x: hidden; }
  
  .chart-container { width: 100% !important; height: 120px !important; }
  .chart-card { overflow: hidden; }
}
</style>
